import { useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Grid, Text } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { BenefitsBenefitAvailabilityFragment } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  BenefitsCardBenefit,
  type BenefitsCardBenefitActions,
} from 'modules/benefits/components/BenefitsCardBenefit/BenefitsCardBenefit'
import { type PerkItem, getAvailablePerks } from 'modules/benefits/lib/perks'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface OutletContext {
  featureAvailabilities: BenefitsBenefitAvailabilityFragment[] | undefined
  perksEnabled: boolean | undefined
}

const PerksPage = () => {
  const { featureAvailabilities, perksEnabled } = useOutletContext<OutletContext>()

  useEffect(() => {
    track(trackingEvents.benefits.openPerksPage)
  }, [])

  const perks = perksEnabled === true ? getAvailablePerks(featureAvailabilities) : []

  const handlePerkAction = (perk: PerkItem) => {
    track(trackingEvents.benefits.requestPerk, { benefit_type: perk.id })

    if (perk.externalUrl) {
      window.open(perk.externalUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const getPerkActions = (perk: PerkItem): BenefitsCardBenefitActions => ({
    ...(perk.externalUrl && {
      primaryAction: {
        label: i18n.t('benefits.my_benefits.perk.actions.redeem'),
        onClick: () => handlePerkAction(perk),
      },
    }),
  })

  return (
    <ResponsivePage.Body showHeader={false} variant="full">
      <ResourceHeader
        title={i18n.t('benefits.my_benefits.perk.title')}
        description={i18n.t('benefits.my_benefits.perk.description')}
      />

      <Box paddingTop="s24" gap="s24">
        {perksEnabled !== true ? (
          <Box paddingTop="s24">
            <Text size="300" weight="normal">
              {i18n.t('benefits.my_benefits.perk.empty_states.not_available')}
            </Text>
          </Box>
        ) : perks.length === 0 ? (
          <Box paddingTop="s24">
            <Text size="300" weight="normal">
              {i18n.t('benefits.my_benefits.perk.empty_states.no_perks')}
            </Text>
          </Box>
        ) : (
          <Box gap="s12" flexDirection="row" flexWrap="wrap">
            <Grid variant="fixed">
              {perks.map((perk) => (
                <Box
                  key={perk.id}
                  gridColSpan={{ xxs: 12, xs: 12, s: 12, m: 4, l: 4, xl: 3, xxl: 3 }}
                >
                  <BenefitsCardBenefit
                    benefitListing={{
                      // @ts-expect-error - Perks use different ID types than regular benefits
                      id: perk.id,
                      title: perk.title,
                      description: perk.description,
                      emoji: perk.emoji,
                      availability: 'active',
                    }}
                    setupStatus={undefined}
                    showTag={false}
                    actions={getPerkActions(perk)}
                  />
                </Box>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default PerksPage
